import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  ReactFlowProvider,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';

import { getFlow, updateFlow, getProject, executeFlow, getFlowExecution } from '../services/api';
import { 
  ArrowLeft, 
  Play, 
  Save, 
  Settings, 
  Zap,
  Target,
  Database,
  MessageSquare,
  BarChart3,
  Clock,
  TrendingUp
} from 'lucide-react';

// Node Types
import PromptNode from '../components/Composer/PromptNode';
import TestSetNode from '../components/Composer/TestSetNode';
import AutoEvaluatorNode from '../components/Composer/AutoEvaluatorNode';
import ResponseNode from '../components/Composer/ResponseNode';
import TextFieldsNode from '../components/Composer/TextFieldsNode';
import VisualizerNode from '../components/Composer/VisualizerNode';
import SplitNode from '../components/Composer/SplitNode';

// Sidebar Components
import NodePalette from '../components/Composer/NodePalette';
import NodeDetailsPanel from '../components/Composer/NodeDetailsPanel';

const nodeTypes = {
  promptNode: PromptNode,
  testSetNode: TestSetNode,
  autoEvaluatorNode: AutoEvaluatorNode,
  responseNode: ResponseNode,
  textFieldsNode: TextFieldsNode,
  visualizerNode: VisualizerNode,
  splitNode: SplitNode,
};

let id = 0;
const getId = () => `node_${id++}`;

// Helper function to update the ID counter based on existing nodes
const updateIdCounter = (existingNodes) => {
  if (existingNodes && existingNodes.length > 0) {
    const maxId = Math.max(
      ...existingNodes
        .map(node => {
          const match = node.id.match(/^node_(\d+)$/);
          return match ? parseInt(match[1], 10) : -1;
        })
        .filter(num => num >= 0)
    );
    if (maxId >= 0) {
      id = maxId + 1;
    }
  }
};

const ComposerFlow = () => {
  const { projectId, flowId } = useParams();
  const navigate = useNavigate();
  const { setViewport } = useReactFlow();
  
  const [project, setProject] = useState(null);
  const [flow, setFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  const [selectedNode, setSelectedNode] = useState(null);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  
  // Execution state
  const [currentExecution, setCurrentExecution] = useState(null);
  const [polling, setPolling] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  useEffect(() => {
    loadData();
  }, [flowId, projectId]);

  // Auto-save when nodes or edges change
  useEffect(() => {
    if (flow && (nodes.length > 0 || edges.length > 0) && !saving && !autoSaving) {
      const timeoutId = setTimeout(() => {
        autoSave();
      }, 20000); // Auto-save after 20 second of no changes

      return () => clearTimeout(timeoutId);
    }
  }, [nodes, edges, flow, saving, autoSaving]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectData, flowData] = await Promise.all([
        getProject(projectId),
        getFlow(flowId)
      ]);
      
      setProject(projectData);
      setFlow(flowData);
      
      // Set initial nodes and edges from flow data
      if (flowData.nodes && Array.isArray(flowData.nodes)) {
        setNodes(flowData.nodes);
        // Update the ID counter to avoid conflicts with existing nodes
        updateIdCounter(flowData.nodes);
      }
      if (flowData.connections && Array.isArray(flowData.connections)) {
        // Convert connections to ReactFlow edges format
        const reactFlowEdges = flowData.connections.map(conn => ({
          id: conn.connection_id || conn.id,
          source: conn.source_node_id || conn.source,
          target: conn.target_node_id || conn.target,
          sourceHandle: conn.source_output || conn.sourceHandle,
          targetHandle: conn.target_input || conn.targetHandle,
        }));
        setEdges(reactFlowEdges);
      }
    } catch (error) {
      console.error('Error loading composer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const onConnect = useCallback(
    (params) => {
      
      // Check if this connection is from a split node
      const sourceNode = nodes.find(n => n.id === params.source);
      let connectionData = { ...params };
      
      if (sourceNode && sourceNode.type === 'splitNode') {

        
        // Find the branch information
        const branch = sourceNode.data.branches?.find(b => b.id === params.sourceHandle);
        if (branch) {
          connectionData = {
            ...params,
            branchId: branch.id,
            branchCondition: branch.condition,
            isConditional: true
          };
        }
      }
      
      const newEdge = {
        ...connectionData,
        id: params.id || `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges, nodes]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      
      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = {
        x: event.clientX - 300, // Adjust for sidebar width
        y: event.clientY - 100, // Adjust for header height
      };

      const newNode = {
        id: getId(),
        type,
        position,
        data: { 
          label: type === 'promptNode' ? 'Prompt Node' : 
                 type === 'testSetNode' ? 'Test Set Node' : 
                 type === 'responseNode' ? 'Response Display' :
                 type === 'textFieldsNode' ? 'TextFields Node' :
                 type === 'visualizerNode' ? 'Visualizer Node' :
                 type === 'splitNode' ? 'Split Node' :
                 'Auto-Evaluator',
          ...(type === 'promptNode' && { 
            promptId: null, 
            versionId: null,
            promptType: 'existing', // or 'custom'
            customTemplate: '',
            templateVariables: []
          }),
          ...(type === 'testSetNode' && { testSetId: null }),
          ...(type === 'responseNode' && { 
            displayMode: 'preview', 
            maxLength: 200,
            showMetadata: true 
          }),
          ...(type === 'textFieldsNode' && {
            fields: [
              {
                name: 'variable1',
                values: ['']
              }
            ]
          }),
          ...(type === 'autoEvaluatorNode' && { 
            evaluation_model: 'llama-3.1-8b-instant', 
            pass_threshold: 0.7, 
            criteria: [
              {
                name: 'Relevance',
                description: 'How well does the response address the input question?',
                weight: 40
              },
              {
                name: 'Accuracy',
                description: 'Is the information provided correct and factual?',
                weight: 35
              },
              {
                name: 'Clarity',
                description: 'Is the response clear and easy to understand?',
                weight: 25
              }
            ]
          }),
          ...(type === 'visualizerNode' && {
            visualizationType: 'table',
            maxDisplayItems: 10,
            showTypes: true,
            autoRefresh: true
          }),
          ...(type === 'splitNode' && {
            splitType: 'condition',
            branches: [
              {
                id: 'branch-1',
                name: 'Branch A',
                condition: {
                  type: 'expression',
                  field: 'response',
                  operator: 'contains',
                  value: ''
                }
              },
              {
                id: 'branch-2',
                name: 'Branch B',
                condition: {
                  type: 'expression',
                  field: 'response',
                  operator: 'contains',
                  value: ''
                }
              }
            ],
            defaultBranch: '',
            aggregationMode: 'all'
          })
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [setNodes]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setRightPanelOpen(true);
  }, []);

  const saveFlowData = async (isAutoSave = false) => {
    try {
      if (isAutoSave) {
        setAutoSaving(true);
      } else {
        setSaving(true);
      }
      
      // Convert edges back to flow connections format
      const connections = edges.map(edge => ({
        id: edge.id,
        connection_id: edge.id,
        source_node_id: edge.source,
        target_node_id: edge.target,
        source_output: edge.sourceHandle || 'output',
        target_input: edge.targetHandle || 'input',
        branchId: edge.branchId || null,
        branchCondition: edge.branchCondition || null,
        isConditional: edge.isConditional || false
      }));


      const updateData = {
        nodes,
        connections
      };


      await updateFlow(flowId, updateData);
      
      if (!isAutoSave) {
      }
    } catch (error) {
      console.error('Error saving flow:', error);
    } finally {
      if (isAutoSave) {
        setAutoSaving(false);
      } else {
        setSaving(false);
      }
    }
  };

  const handleSave = () => saveFlowData(false);
  const autoSave = () => {
    // Prevent multiple simultaneous auto-saves
    if (saving || autoSaving) {
      return;
    }
    saveFlowData(true);
  };

  const handleExecute = async () => {
    try {
      setIsExecuting(true);
      
      // Validate auto-evaluator nodes have proper weights
      const autoEvaluatorNodes = nodes.filter(node => node.type === 'autoEvaluatorNode');
      for (const node of autoEvaluatorNodes) {
        const criteria = node.data.criteria || [];
        if (criteria.length > 0) {
          const totalWeight = criteria.reduce((sum, c) => sum + (c.weight || 0), 0);
          if (totalWeight !== 100) {
            throw new Error(`Auto-Evaluator "${node.data.label || node.id}" has invalid weights. Criteria weights must add up to 100% (currently: ${totalWeight}%)`);
          }
        }
      }
      
      // Execute with empty inputs (test sets will provide inputs automatically)
      const executionResult = await executeFlow(flowId, {});
      
      setCurrentExecution(executionResult);
      setRightPanelOpen(false);
      
      // Start polling for updates if execution is running
      if (executionResult.status === 'running') {
        startPolling(executionResult.executionId);
      }
    } catch (error) {
      console.error('Error executing flow:', error);
      alert(`Execution failed: ${error.message}`);
    } finally {
      setIsExecuting(false);
    }
  };

  const startPolling = (executionId) => {
    if (polling) return; // Don't start multiple polling intervals
    
    setPolling(true);
    
    const pollInterval = setInterval(async () => {
      try {
        const execution = await getFlowExecution(flowId, executionId);
        setCurrentExecution(execution);
        
        // Update nodes with execution results
        if (execution.nodeResults && execution.nodeResults.length > 0) {
          updateNodesWithResults(execution.nodeResults);
        }
        
        // Stop polling if execution is complete
        if (execution.status === 'completed' || execution.status === 'failed') {
          clearInterval(pollInterval);
          setPolling(false);
        }
      } catch (error) {
        console.error('Error polling execution status:', error);
        clearInterval(pollInterval);
        setPolling(false);
      }
    }, 2000); // Poll every 2 seconds
    
    return pollInterval;
  };

  const updateNodesWithResults = (nodeResults) => {
    
    setNodes((nds) =>
      nds.map((node) => {
        const nodeResult = nodeResults.find(result => result.node_id === node.id);
        if (nodeResult) {
          
          // Parse the result if it's a string
          let parsedResult = nodeResult.result;
          if (typeof nodeResult.result === 'string') {
            try {
              parsedResult = JSON.parse(nodeResult.result);
            } catch (e) {
              console.error('Error parsing node result:', e);
              parsedResult = nodeResult.result;
            }
          }
          
          return {
            ...node,
            data: {
              ...node.data,
              executionResult: parsedResult,
              executionStatus: nodeResult.status
            }
          };
        }
        return node;
      })
    );
    
    // Also update selected node if it has results
    if (selectedNode) {
      const nodeResult = nodeResults.find(result => result.node_id === selectedNode.id);
      if (nodeResult) {
        let parsedResult = nodeResult.result;
        if (typeof nodeResult.result === 'string') {
          try {
            parsedResult = JSON.parse(nodeResult.result);
          } catch (e) {
            console.error('Error parsing selected node result:', e);
            parsedResult = nodeResult.result;
          }
        }
        
        setSelectedNode(prev => ({
          ...prev,
          data: {
            ...prev.data,
            executionResult: parsedResult,
            executionStatus: nodeResult.status
          }
        }));
      }
         }
   };



  const closeResults = () => {
    setCurrentExecution(null);
  };

  const openFlowResults = () => {
    if (currentExecution) {
      const executionId = currentExecution.id || currentExecution.executionId;
      navigate(`/projects/${projectId}/composer/${flowId}/results/${executionId}`);
    }
  };

  const onNodeDataChange = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...newData } } : node
      )
    );
    
    // Update selected node if it's the one being changed
    if (selectedNode && selectedNode.id === nodeId) {
      setSelectedNode(prev => ({
        ...prev,
        data: { ...prev.data, ...newData }
      }));
    }
  }, [setNodes, selectedNode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner h-12 w-12 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading composer...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/projects/${projectId}/composer`)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-celadon-100 rounded-xl">
              <Zap className="h-6 w-6 text-celadon-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{flow?.name}</h1>
              <p className="text-sm text-gray-600">{project?.name}</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleSave}
            disabled={saving || autoSaving}
            className="btn-secondary"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : autoSaving ? 'Auto-saving...' : 'Save'}
          </button>
          
          <button
            onClick={handleExecute}
            disabled={isExecuting}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Play className="h-4 w-4 mr-2" />
            {isExecuting ? 'Executing...' : 'Execute'}
          </button>
          
          {currentExecution && currentExecution.status === 'completed' && (
            <button
              onClick={openFlowResults}
              className="btn-secondary"
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Flow Results
            </button>
          )}
          
          {currentExecution && currentExecution.status === 'running' && (
            <div className="flex items-center px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600">
              <Clock className="h-4 w-4 mr-2 animate-spin" />
              Execution in progress...
            </div>
          )}
          
          
          
          <button className="btn-ghost">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Node Palette */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Node Palette</h2>
            <p className="text-sm text-gray-600 mt-1">
              Drag nodes onto the canvas to build your flow
            </p>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            <NodePalette />
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            className="bg-gray-50"
          >
            <Controls />
            <MiniMap />
            <Background variant="dots" gap={12} size={1} />
            
            <Panel position="top-left" className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <div className="text-sm text-gray-600">
                <strong>Tip:</strong> Drag nodes from the palette to add them to your flow
              </div>
            </Panel>
          </ReactFlow>
        </div>

        {/* Right Sidebar - Node Details */}
        {rightPanelOpen && (
          <div className="w-96 bg-white border-l border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Node Details</h2>
              <button
                onClick={() => setRightPanelOpen(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <span className="text-gray-400">×</span>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {selectedNode && (
                <NodeDetailsPanel
                  node={selectedNode}
                  projectId={projectId}
                  onNodeDataChange={onNodeDataChange}
                />
              )}
            </div>
          </div>
        )}
      </div>


    </div>
  );
};

const Composer = () => {
  return (
    <ReactFlowProvider>
      <ComposerFlow />
    </ReactFlowProvider>
  );
};

export default Composer; 